/**
 * A LISTA DO DISPUTAR (L7, decisões do Heitor de 24/10/2026: O1, E1, L1, R1, V1, D1).
 *
 * Funções puras: o DisputeView só desenha o que sai daqui.
 *
 * - O1: a lista se divide em Hoje / ao vivo, Próximas e Finalizadas. As "configurando" presas (sessão que já passou e
 *   nunca rodou) NÃO vão para Hoje nem para Próximas: vão para Finalizadas, onde o "Encerrar sessões presas" as acha.
 * - E1: cada cartão tem UM estado, derivado da data e do resultado. O resto (REAL, servidor, piso, avisos) vai para o
 *   detalhe.
 * - V1: vencedora é só a CONFIRMADA: desfecho "ganho" registrado, ou a licitação homologada/adjudicada com a IPCOM em 1º.
 *   Ficar em 1º no ranking não basta (a SUREG Bahia foi 1ª e não foi convocada); 1º sem confirmação pede ação.
 * - L1: a proposta enviada com sessão marcada e SEM disputa no Hub vira um cartão próprio, em âmbar, dentro de Próximas.
 */

import { falhaIgnoradaDeProposito } from '../design/cargaDaLista';

export type SecaoDaDisputa = 'hoje' | 'proximas' | 'finalizadas';
export type TomDoEstado = 'live' | 'warning' | 'success' | 'neutral';
export interface EstadoDoCartao { rotulo: string; tom: TomDoEstado; chave: string }

/** A janela das Próximas que o resumo conta (O1). */
export const JANELA_PROXIMAS_H = 72;
/** Finalizadas abertas mostram os últimos 30 dias; o resto fica no "ver mais antigas". */
export const DIAS_DAS_FINALIZADAS_RECENTES = 30;

const ms = (v: any): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/** O início da sessão: o da disputa, senão o do item (sessão pública), senão o fim das propostas. */
export const inicioDaSessao = (s: any): number | null =>
  ms(s?.disputeStartTime) ?? ms(s?.item?.dataSessaoInicio) ?? ms(s?.item?.dataEncerramento);
export const fimDaSessao = (s: any): number | null => ms(s?.disputeEndTime) ?? ms(s?.item?.dataSessaoFim);

const mesmoDia = (a: number, b: number) => {
  const x = new Date(a), y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};

/** Em que seção a sessão mora. */
export function secaoDaSessao(s: any, agora: number): SecaoDaDisputa {
  const st = String(s?.status || '');
  if (st === 'ativo' || st === 'aguardando') return 'hoje';
  if (st === 'finalizado' || st === 'erro') return 'finalizadas';
  const ini = inicioDaSessao(s);
  const fim = fimDaSessao(s);
  if (ini === null) return 'finalizadas';
  if (ini > agora) return mesmoDia(ini, agora) ? 'hoje' : 'proximas';
  // começou (ou deveria): só é "hoje" se é do dia e ainda não terminou; senão é uma sessão presa
  if (mesmoDia(ini, agora) && (fim === null || fim > agora)) return 'hoje';
  return 'finalizadas';
}

/** V1: a vitória confirmada. `ipcomEm1o` vem do ranking canônico (computeIpcomOutcome). */
export function vitoriaConfirmada(s: any, ipcomEm1o: boolean): boolean {
  const it = s?.item || {};
  if (String(it.desfecho || '').toLowerCase() === 'ganho') return true;
  return ipcomEm1o && ['homologado', 'adjudicado'].includes(String(it.status || '').toLowerCase());
}

/** O valor ganho de uma vitória confirmada: o homologado, senão o total da proposta. */
export function valorGanho(s: any): number {
  const h = Number(s?.item?.valorHomologado);
  if (Number.isFinite(h) && h > 0) return h;
  const p = Number(s?.proposal?.totalValue);
  return Number.isFinite(p) ? p : 0;
}

/** "em 1d 11h", "em 3h 05min", "em 12min". */
export function contagem(ate: number, agora: number): string {
  const min = Math.max(0, Math.round((ate - agora) / 60000));
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  if (d > 0) return `em ${d}d ${h}h`;
  if (h > 0) return `em ${h}h ${String(m).padStart(2, '0')}min`;
  return `em ${m}min`;
}

export interface EntradaDoEstado {
  secao: SecaoDaDisputa;
  agora: number;
  status: string;
  inicio: number | null;
  vitoria: boolean;
  classif: number | null;
  ipcomEm1o: boolean;
  /** true/false do getPhaseInfo; null = não se sabe */
  convocado: boolean | null;
  desclassificado: boolean;
  resultadoPendente: boolean;
  compraEncerrada: boolean;
}

/** E1: o estado único do cartão, na ordem: ao vivo, hoje, próxima, e depois o resultado. */
export function estadoDoCartao(e: EntradaDoEstado): EstadoDoCartao {
  const pos = e.classif ? ` · ${e.classif}º` : '';
  if (e.status === 'ativo' || e.status === 'aguardando') return { chave: 'ao-vivo', rotulo: 'Ao vivo', tom: 'live' };
  if (e.secao === 'hoje') {
    const hm = e.inicio ? new Date(e.inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }) : '';
    return { chave: 'hoje', rotulo: hm ? `Hoje · ${hm}` : 'Hoje', tom: 'neutral' };
  }
  if (e.secao === 'proximas') return { chave: 'proxima', rotulo: e.inicio ? contagem(e.inicio, e.agora) : 'Próxima', tom: 'neutral' };
  if (e.vitoria) return { chave: 'vencedor', rotulo: `Vencedor${pos}`, tom: 'success' };
  if (e.desclassificado) return { chave: 'desclassificado', rotulo: 'Desclassificado', tom: 'neutral' };
  if (e.convocado === false && e.classif) return { chave: 'nao-convocado', rotulo: `Não convocado${pos}`, tom: 'neutral' };
  if (e.ipcomEm1o) return { chave: 'confirmar', rotulo: '1º · confirmar resultado', tom: 'warning' };
  if (e.convocado === true) return { chave: 'convocado', rotulo: `Convocado${pos}`, tom: 'warning' };
  if (e.classif) return { chave: 'nao-venceu', rotulo: `Não venceu${pos}`, tom: 'neutral' };
  if (e.compraEncerrada) return { chave: 'compra-encerrada', rotulo: 'Compra encerrada', tom: 'neutral' };
  return { chave: 'aguardando-resultado', rotulo: 'Aguardando resultado', tom: 'neutral' };
}

/**
 * L1: a proposta que deveria ter disputa e não tem. Mesma regra do alvo âmbar do Cadastrar (faltaMandarParaDisputa):
 * enviada, Pregão ou Dispensa pelo NOME da modalidade, sessão no futuro e sem disputeSessionId. O item que já tem sessão
 * (de outra proposta) não entra: o backend recusa a segunda.
 */
export function propostasSemDisputa(propostas: any[], sessoes: any[], agora: number): any[] {
  const comSessao = new Set((sessoes || []).map((s: any) => s?.itemId).filter(Boolean));
  return (propostas || []).filter((p: any) => {
    if (!p?.item?.id || p.disputeSessionId || comSessao.has(p.item.id)) return false;
    if (p.status !== 'enviada') return false;
    if (!/preg[ãa]o|dispensa/i.test(String(p.item.modalidadeNome || ''))) return false;
    const quando = ms(p.item.dataSessaoInicio) ?? ms(p.item.dataEncerramento);
    return quando !== null && quando > agora;
  }).sort((a: any, b: any) => (ms(a.item.dataSessaoInicio) ?? ms(a.item.dataEncerramento) ?? 0) - (ms(b.item.dataSessaoInicio) ?? ms(b.item.dataEncerramento) ?? 0));
}

/** O cabeçalho das Finalizadas fechadas: "Finalizadas · 27 · 1 vencedora · R$ 13.800,00 ganhos". */
export function resumoDasFinalizadas(n: number, vencedoras: number, ganho: number, fmtMoeda: (v: number) => string): string {
  const v = vencedoras === 1 ? '1 vencedora' : `${vencedoras} vencedoras`;
  return `Finalizadas · ${n} · ${v} · ${fmtMoeda(ganho)} ganhos`;
}

/** Lembra aberto/fechado das Finalizadas por navegador; localStorage pode faltar ou lançar. */
const CHAVE_FINALIZADAS = 'hub.disputar.finalizadasAbertas';
export function lerFinalizadasAbertas(): boolean {
  try { return window.localStorage.getItem(CHAVE_FINALIZADAS) === '1'; } catch (e) {
    falhaIgnoradaDeProposito('navegador sem localStorage (aba anônima, bloqueio): as Finalizadas abrem fechadas, o padrão')(e);
    return false;
  }
}
export function gravarFinalizadasAbertas(aberta: boolean): void {
  try { window.localStorage.setItem(CHAVE_FINALIZADAS, aberta ? '1' : '0'); } catch (e) {
    falhaIgnoradaDeProposito('navegador sem localStorage: a seção abre e fecha igual, só não lembra na próxima visita')(e);
  }
}
